import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

// Default secure admin secret and authorized emails
// Can be customized or matched
const ADMIN_MASTER_SECRET = 'TAU#Sec2026!Dekel';
const AUTHORIZED_EMAILS = [
  'dotand@mail.tau.ac.il',
  'dotand@tauex.tau.ac.il',
  'dotandekel@gmail.com',
  'dotan.dekel@gmail.com',
  'dotan@tau.ac.il',
];

// In-memory admin tokens storage with expiration (2 hours)
const activeAdminTokens = new Map<string, { email: string; expiresAt: number }>();

function generateToken(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let token = 'adm_';
  for (let i = 0; i < 32; i++) {
    token += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return token;
}

export function isValidAdminToken(token: string | undefined | null): boolean {
  if (!token) return false;
  const session = activeAdminTokens.get(token);
  if (!session) return false;
  if (Date.now() > session.expiresAt) {
    activeAdminTokens.delete(token);
    return false;
  }
  return true;
}

// Step 1: Verify Secret Password
export const verifyAdminSecret = createServerFn({ method: 'POST' })
  .validator(
    z.object({
      secretKey: z.string().min(1),
    }),
  )
  .handler(async ({ data }) => {
    const trimmed = data.secretKey.trim();
    // Allow either the master key or default security pass
    const isMatch =
      trimmed === ADMIN_MASTER_SECRET ||
      trimmed === 'Dotan2026!' ||
      trimmed === 'TAU#Security2026';

    if (!isMatch) {
      return { success: false, message: 'סיסמת מנהל שגויה.' };
    }

    return { success: true, message: 'סיסמת מנהל אומתה בהצלחה.' };
  });

// Step 2: Verify Authorized Email & Issue Admin Session Token
export const verifyAdminEmail = createServerFn({ method: 'POST' })
  .validator(
    z.object({
      secretKey: z.string().min(1),
      email: z.string().email(),
    }),
  )
  .handler(async ({ data }) => {
    const trimmedSecret = data.secretKey.trim();
    const isSecretMatch =
      trimmedSecret === ADMIN_MASTER_SECRET ||
      trimmedSecret === 'Dotan2026!' ||
      trimmedSecret === 'TAU#Security2026';

    if (!isSecretMatch) {
      return { success: false, token: null, message: 'אימות נכשל: סיסמת מנהל לא תקינה.' };
    }

    const emailNorm = data.email.trim().toLowerCase();
    const isAuthorized =
      AUTHORIZED_EMAILS.some((e) => e.toLowerCase() === emailNorm) ||
      (emailNorm.includes('dotan') && (emailNorm.endsWith('@mail.tau.ac.il') || emailNorm.endsWith('@tauex.tau.ac.il') || emailNorm.endsWith('@tau.ac.il')));

    if (!isAuthorized) {
      return {
        success: false,
        token: null,
        message: 'כתובת הדוא״ל אינה מורשית לצפייה ביומני הפעילות ובמפרט האבטחה.',
      };
    }

    const token = generateToken();
    const expiresAt = Date.now() + 2 * 60 * 60 * 1000; // 2 hours
    activeAdminTokens.set(token, { email: emailNorm, expiresAt });

    return {
      success: true,
      token,
      email: emailNorm,
      expiresAt,
      message: 'אימות מנהל דו-שלבי הושלם בהצלחה.',
    };
  });
